import {
  IamGroupType,
  OrganizationType,
  type TireHotelFilterCountsApiData,
  type TireHotelListApiData,
  UserGroupCarRole
} from '@navio/nsdk';
import {
  type FilterLinkOf,
  filterUnitFor,
  filterUrlState,
  type SharedQuery,
  useFilterSpecValue,
  useFilterUnits
} from 'noddi-ui-web';
import { CUSTOMER_CONTRACT_STATUS_OPTIONS, TIRE_SEASON_OPTIONS } from 'noddi-util';
import { useAccountOptions } from '../Account/useAccountFilter';
import { VEHICLE_CATEGORY_FILTER_OPTIONS } from '../Car/useVehicleCategoryFilter';
import { ORGANIZATION_FILTER_TEXT, useOrganizationOptions } from '../Organization/useOrganizationFilter';
import { ORGANIZATION_TYPE_FILTER_OPTIONS } from '../Organization/useOrganizationTypeFilter';
import { useSalesItemOptions } from '../SalesItem/useSalesItemsFilter';
import {
  SERVICE_DEPARTMENT_FILTER_TEXT,
  useServiceDepartmentOptions
} from '../ServiceDepartment/useServiceDepartmentFilter';
import { useStorageAreaOptions } from '../StorageArea/useStorageAreasFilter';
import { STORAGE_FACILITY_FILTER_TEXT, useStorageFacilityOptions } from '../StorageFacility/useStorageFacilitiesFilter';
import { useStorageUnitOptions } from '../StorageUnit/useStorageUnitsFilter';
import {
  DATA_QUALITY_ISSUE_TYPES_FILTER,
  HAS_DATA_QUALITY_ISSUES_FILTER_TEXT
} from '../UserGroup/useUserGroupDataQualityFilters';
import { useUserGroupOptions } from '../UserGroup/useUserGroupFilter';
import { useTireHotelFilterCounts } from './useTireHotelFilterCounts';

// The list and its facet counts read the same input.
type TireHotelListFilterInput = Omit<
  SharedQuery<NonNullable<TireHotelListApiData['query']>, NonNullable<TireHotelFilterCountsApiData['query']>>,
  'pageIndex' | 'pageSize' | 'ordering' | 'search'
>;

const tireHotelFilterUnit = filterUnitFor<TireHotelListFilterInput>();

// Adapter-synced virtual area name until the backend renames it.
const HIDDEN_AREA_NAMES = ['EonTyre'];

/**
 * The tire hotel list's filters with their facet counts: spread `apiInput` into the list (the counts already
 * read it). Partner and facility scope the list with `serviceDepartmentIds` / `scopedStorageFacilityIds`.
 */
export const useTireHotelsTableFilters = ({
  serviceDepartmentIds: scopedServiceDepartmentIds,
  showServiceDepartmentFilter = false,
  scopedStorageFacilityIds
}: {
  serviceDepartmentIds?: number[];
  showServiceDepartmentFilter?: boolean;
  scopedStorageFacilityIds?: number[];
} = {}) => {
  const serviceDepartmentSpec = tireHotelFilterUnit.multiSelect('serviceDepartmentIds', {
    ...SERVICE_DEPARTMENT_FILTER_TEXT,
    description: 'Show tire hotels belonging to these service departments.',
    source: useServiceDepartmentOptions({ enabled: showServiceDepartmentFilter }),
    exclude: true
  });
  const { serviceDepartmentIds: pickedDepartmentIds } = useFilterSpecValue(serviceDepartmentSpec);
  const departmentIds = showServiceDepartmentFilter ? pickedDepartmentIds : scopedServiceDepartmentIds;

  const hideStorageFacilityFilter = scopedStorageFacilityIds != null && scopedStorageFacilityIds.length > 0;
  const storageFacilitySpec = tireHotelFilterUnit.multiSelect('storageFacilityIds', {
    ...STORAGE_FACILITY_FILTER_TEXT,
    title: 'Preferred facility',
    description: 'Show tire hotels whose preferred storage facility is one of these.',
    source: useStorageFacilityOptions({ serviceDepartmentIds: departmentIds, heldBy: 'storageFacilityIds' }),
    exclude: true
  });
  const { storageFacilityIds: pickedFacilityIds } = useFilterSpecValue(storageFacilitySpec);
  const facilityIds = hideStorageFacilityFilter ? scopedStorageFacilityIds : pickedFacilityIds;

  const storageAreaSpec = tireHotelFilterUnit.multiSelect('storageAreaIds', {
    title: 'Storage area',
    description: 'Show tire hotels whose stored wheel set is in these hotel areas.',
    defaultVisibleOptions: 3,
    source: useStorageAreaOptions({
      storageFacilityIds: facilityIds,
      serviceDepartmentIds: departmentIds,
      excludeAreaNames: HIDDEN_AREA_NAMES
    }),
    exclude: true
  });
  const { storageAreaIds } = useFilterSpecValue(storageAreaSpec);

  const filters = useFilterUnits([
    // The department scope is sent even when its control is hidden.
    showServiceDepartmentFilter
      ? serviceDepartmentSpec
      : tireHotelFilterUnit.locked({ serviceDepartmentIds: scopedServiceDepartmentIds }),
    tireHotelFilterUnit.multiSelect('organizationIds', {
      ...ORGANIZATION_FILTER_TEXT,
      source: useOrganizationOptions(),
      exclude: true
    }),
    tireHotelFilterUnit.multiSelect('organizationTypes', {
      title: 'Customer organization type',
      description:
        'Show tire hotels whose customer belongs to an organization of these types. Leasing company and car dealership also include hotels whose car is currently owned by that type.',
      options: ORGANIZATION_TYPE_FILTER_OPTIONS,
      exclude: true
    }),
    invoiceToFilter,
    tireHotelFilterUnit.boolean('isLeased', {
      title: 'Leased',
      description: 'Show cars that are leased (owned by a leasing company or car dealership).'
    }),
    tireHotelFilterUnit.multiSelect('leaseholderIds', {
      title: 'Leaseholder',
      description: 'Filter by the personal customer leasing the car (leaseholder).',
      source: useUserGroupOptions({ carRoles: [UserGroupCarRole.LEASEHOLDER] }),
      defaultVisibleOptions: 3,
      exclude: true
    }),
    tireHotelFilterUnit.multiSelect('leasingCompanyIds', {
      title: 'Leasing company',
      description: 'Show records whose car is owned by these leasing companies.',
      source: useOrganizationOptions({ organizationTypes: [OrganizationType.LEASING_COMPANY] }),
      exclude: true
    }),
    tireHotelFilterUnit.multiSelect('carDealershipIds', {
      title: 'Car dealership',
      description: 'Show records whose car is associated with these car dealerships (selling dealer).',
      source: useOrganizationOptions({ organizationTypes: [OrganizationType.CAR_DEALERSHIP] }),
      exclude: true
    }),
    tireHotelFilterUnit.multiSelect('userGroupIds', {
      title: 'Groups',
      description: 'Filter by specific groups',
      source: useUserGroupOptions({ types: [IamGroupType.GROUP] }),
      defaultVisibleOptions: 3,
      exclude: true
    }),
    tireHotelFilterUnit.boolean('hasDataQualityIssues', HAS_DATA_QUALITY_ISSUES_FILTER_TEXT),
    tireHotelFilterUnit.multiSelect('dataQualityIssueTypes', { ...DATA_QUALITY_ISSUE_TYPES_FILTER, exclude: true }),
    tireHotelFilterUnit.multiSelect('carManagerIds', {
      title: 'Driver',
      description: 'Filter by people who are a driver or car manager for the car.',
      source: useAccountOptions(),
      exclude: true
    }),
    tireHotelFilterUnit.multiSelect('statuses', {
      title: 'Status',
      description: 'Show tire hotels with these contract statuses.',
      options: CUSTOMER_CONTRACT_STATUS_OPTIONS,
      exclude: true
    }),
    tireHotelFilterUnit.boolean('hasOpenCustody', {
      title: 'Stored wheel set',
      description: 'Show tire hotels whose car currently has wheels in storage.'
    }),
    tireHotelFilterUnit.multiSelect('inStockSeasons', {
      title: 'In stock season',
      description: 'Show tire hotels whose stored tires match these seasons.',
      options: TIRE_SEASON_OPTIONS,
      exclude: true
    }),
    tireHotelFilterUnit.multiSelect('vehicleCategories', {
      title: 'Wheel topology',
      description: 'Show tire hotels whose car has this wheel layout.',
      options: VEHICLE_CATEGORY_FILTER_OPTIONS,
      exclude: true
    }),
    tireHotelFilterUnit.boolean('isStaggered', {
      title: 'Staggered fitment',
      description: 'Show tire hotels whose stored wheel set has different front and rear sizes.'
    }),
    excludedCustomerFilter,
    tireHotelFilterUnit.boolean('autoRenew', {
      title: 'Auto renew',
      description: 'Show tire hotels that do or do not auto-renew at period end.'
    }),
    // A scoped facility replaces the control and is always sent.
    hideStorageFacilityFilter
      ? tireHotelFilterUnit.locked({ storageFacilityIds: scopedStorageFacilityIds })
      : storageFacilitySpec,
    storageAreaSpec,
    tireHotelFilterUnit.multiSelect('storageUnitIds', {
      title: 'Storage unit',
      description: 'Show tire hotels whose stored wheel set is at these hotel bins.',
      defaultVisibleOptions: 3,
      source: useStorageUnitOptions({
        storageFacilityIds: facilityIds,
        storageAreaIds,
        serviceDepartmentIds: departmentIds
      }),
      exclude: true
    }),
    tireHotelFilterUnit.multiSelect('salesItemIds', {
      title: 'Products',
      description: 'Show tire hotels billed on these products.',
      source: useSalesItemOptions({ serviceDepartmentIds: departmentIds }),
      exclude: true
    }),
    tireHotelFilterUnit.text('externalTyreHotelId', {
      title: 'External tire hotel id',
      description: 'Show the tire hotel with this adapter external tire hotel id.',
      placeholder: 'External id'
    }),
    tireHotelFilterUnit.dateRange('createdAt', {
      title: 'Created at',
      description: 'Show tire hotels created in this date range.',
      exclude: true
    }),
    tireHotelFilterUnit.dateRange('updatedAt', {
      title: 'Updated at',
      description: 'Show tire hotels last updated in this date range.',
      exclude: true
    })
  ]);

  return useTireHotelFilterCounts(filters);
};

/** What a link into the tire hotel list may set (`filterSearch`). */
export type TireHotelListLink = FilterLinkOf<typeof useTireHotelsTableFilters>;

/** `excludedCustomer` is a flag of its own (left out of recall generation), not an exclusion: there is no `customer`. */
const excludedCustomerFilter = tireHotelFilterUnit.boolean('excludedCustomer', {
  title: 'Excluded customer',
  description: 'Show tire hotels excluded from recall generation.'
});

const PAYER_URL_KEYS = {
  include: filterUrlState.urlKey('userGroupPayerIds'),
  exclude: filterUrlState.urlKey('excludedUserGroupPayerIds')
};

/**
 * Invoice to. Its options and counts come from the filter-counts endpoint, which reads the whole filter input
 * (this control included), so no option source can exist before the control: the counts fill them in.
 */
const invoiceToFilter = tireHotelFilterUnit.compose(
  'userGroupPayerIds',
  {
    [PAYER_URL_KEYS.include]: filterUrlState.selection(filterUrlState.parseNumber),
    [PAYER_URL_KEYS.exclude]: filterUrlState.excluded(filterUrlState.parseNumber)
  },
  (values, setValues) => {
    const state = {
      include: values[PAYER_URL_KEYS.include] as number[] | null | undefined,
      exclude: (values[PAYER_URL_KEYS.exclude] as number[] | undefined) ?? []
    };
    const included = filterUrlState.includedValues(state, []);
    const write = (include: number[], exclude: number[]) =>
      setValues(() => ({
        [PAYER_URL_KEYS.include]: include.length > 0 ? include : undefined,
        [PAYER_URL_KEYS.exclude]: exclude
      }));
    return {
      controls: {
        id: 'userGroupPayerIds',
        type: 'multi-select',
        title: 'Invoice to',
        description: 'Filter by the organization the customer is invoiced to (Faktureres til).',
        options: [],
        canExclude: true,
        value: { include: included, exclude: state.exclude },
        onChange: (next) => {
          const exclude = next.exclude.map(Number);
          write(
            next.include.map(Number).filter((value) => !exclude.includes(value)),
            exclude
          );
        },
        reset: () => write([], []),
        isInitial: state.include === undefined && state.exclude.length === 0
      },
      params: {
        userGroupPayerIds: included.length > 0 ? included : undefined,
        excludedUserGroupPayerIds: state.exclude.length > 0 ? state.exclude : undefined
      }
    };
  }
);
